import { CdkDrag, CdkDragEnd, CdkDragHandle, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { Component, HostListener, Inject, inject, signal, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ResizableDirective } from '@app/core/directives/resizable.directive';
import { FileUser } from '@app/core/models/files';
import { FilemanagerApiService } from '@app/core/services/api/filemanager/filemanager-api.service';
import { FilemanagerPinnableStateService } from '@app/core/services/filemanager-pinnable-state/filemanager-pinnable-state.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  ContentComponent,
  ExplorerService
} from 'filemanager-explorer';
import { catchError, EMPTY, filter, from, iif, map, switchMap, tap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { ModalsCutPdfComponent } from '../modals-cut-pdf/modals-cut-pdf.component';
import { ModalsSendFileComponent } from '../modals-send-file/modals-send-file.component';


@Component({
  selector: 'app-modal-file-manager',
  imports: [MatDividerModule,
    CdkDrag,
    CdkDragHandle,
    MatDialogClose, MatDialogTitle, MatDialogContent, MatButtonModule,
    TablerIconsModule,
    MatIconModule,
    ModalsSendFileComponent,
    ModalsCutPdfComponent,
    ContentComponent,
    ResizableDirective,
    MatTooltipModule,
    TranslateModule

  ],
  templateUrl: './modal-file-manager.component.html',
  styleUrl: './modal-file-manager.component.scss'
})
export class ModalFileManagerComponent {
  scrollStrategy: ScrollStrategy;
  fileEdit$ = signal<FileUser | null>({});
  fileType$ = signal<'file' | 'pdf'>('file');
  pinned$ = signal<boolean>(false);
  public _explorerService = inject(ExplorerService);
  private fileManagerService = inject(FilemanagerApiService);
  protected openedDir$ = this._explorerService.openedDir$.pipe(map((p) => p?.data['name']));
  private dialogRef: MatDialogRef<ModalFileManagerComponent> = inject(MatDialogRef);

  // Add these properties to your component class
  private resizeObserver!: ResizeObserver;
  private isResizing = false;
  @ViewChild('dialogSurface') dialogSurface!: ElementRef;
  private lastWidth = 0;
  private lastHeight = 0;

  public dataModule;
  // constructor(@Inject(MAT_DIALOG_DATA) public data: any,
  //   private readonly scrollStrategyOptions: ScrollStrategyOptions,
  //   private renderer: Renderer2,
  //   private elementRef: ElementRef,
  //     private filemanagerPinnableStateService: FilemanagerPinnableStateService) {

  //   this.dataModule = data;
  //   this._explorerService.openNode();
  //   this.scrollStrategy = this.scrollStrategyOptions.noop();


  // }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly scrollStrategyOptions: ScrollStrategyOptions,
    private filemanagerPinnableStateService: FilemanagerPinnableStateService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
  ) {

    this.dataModule = data;
    this._explorerService.openNode();
    this.scrollStrategy = this.scrollStrategyOptions.noop();

    this.initFilemanagerPinnableState();

  }

  protected openFileSub = this._explorerService.openFile$.pipe(
    // tap((f)=> console.log('openFileSubmodal', f)),
    filter(f => !!f?.isLeaf && !!f?.data['uuid'] && !!f?.data['name']),
    switchMap(f => {
      const uuid = f?.data['uuid'];
      const name = f?.data['name'];

      return iif(
        () => f?.data['content_type'] === "application/pdf",
        from(this.fileManagerService.cutPagePdfPromise(uuid, 1)).pipe(
          switchMap(() =>
            this.fileManagerService.getFileBytes(uuid).pipe(
              map(response => ({ data: response, name, uuid })),
              filter((res): res is { data: any; name: string; uuid: string } => !!res.data),
              tap(res => this.fileEdit$.update(() => res)),
              tap(() => this.fileType$.set('pdf')),
              catchError(error => {
                console.error('Errore nel recupero file:', error);
                return EMPTY;
              })
            )
          )
        ),
        this.fileManagerService.getFileBytes(uuid).pipe(
          map(response => ({ data: response, name, uuid })),
          filter((res): res is { data: any; name: string; uuid: string } => !!res.data),
          tap(res => this.fileEdit$.update(() => res)),
          tap(() => this.fileType$.set('file')),
          catchError(error => {
            console.error('Errore nel recupero file:', error);
            return EMPTY;
          })
        )
      );
    })
  ).subscribe();


  // ngAfterViewInit() {
  //   setTimeout(() => {
  //     this.setupResizeObserver();
  //   }, 100);
  // }

  close(): void {
    this.dialogRef.close();
    this.fileEdit$.set(null);
  }

  initFilemanagerPinnableState() {
    this.filemanagerPinnableStateService.filemanagerPinnedState$.subscribe(state => {
      // console.log('pinnedState', state)
      this.pinned$.set(state);
    });
  }

  pinDialog(): void {
    this.pinned$.update(p => !p);
    // this.sharedDataService.storeData({
    //   name: 'modalpinned',
    //   data: this.pinned$()
    // });
    this.filemanagerPinnableStateService.setState(this.pinned$());
    this.dialogRef.close({ modalpinned: this.pinned$() });
  }


  closeEditFile(): void {
    this.fileEdit$.set(null);
    this._explorerService.openFile(undefined);
  }

  onDragStarted(ev: CdkDragStart) {
    ev.event.stopPropagation(); // qui il drag è già avviato
    const body = document.body;
    body.classList.add('modal-file-dragging');
  }

  onDragMoved(ev: CdkDragMove) {
    ev.event.stopPropagation(); // impedisce bubbling verso la modale
  }

  onDragEnded(ev: CdkDragEnd) {
    ev.event.stopPropagation();
    const body = document.body;
    body.classList.remove('modal-file-dragging');
  }


  private setupResizeObserver() {
    const dialogSurface = this.elementRef.nativeElement.closest('.mat-mdc-dialog-surface');
    if (!dialogSurface) return;

    let resizeEndTimeout: any;

    this.resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      const isCurrentlyResizing = width !== this.lastWidth || height !== this.lastHeight;

      if (isCurrentlyResizing) {
        // Reset eventuale timeout di "fine resize"
        if (resizeEndTimeout) {
          clearTimeout(resizeEndTimeout);
          resizeEndTimeout = null;
        }

        if (!this.isResizing) {
          // Resize appena iniziato
          this.isResizing = true;
          this.renderer.addClass(dialogSurface, 'dialog-resizing');
        }
      }

      // Aggiorna le dimensioni
      this.lastWidth = width;
      this.lastHeight = height;

      // Se non cambia più, schedule la fine del resize
      resizeEndTimeout = setTimeout(() => {
        if (this.isResizing) {
          this.isResizing = false;
          this.renderer.removeClass(dialogSurface, 'dialog-resizing');
        }
        resizeEndTimeout = null;
      }, 150); // piccolo debounce
    });

    this.resizeObserver.observe(dialogSurface);
  }

  // Don't forget to clean up the timeout in ngOnDestroy
  ngOnDestroy() {
    this.openFileSub.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // if (resizeEndTimeout) {
    //   clearTimeout(resizeEndTimeout);
    // }
  }


}
